import { Alert, Input, Button, Modal, message, Space } from 'antd';
import { useEffect, useRef, useState } from 'react';
import NiceModal from '@ebay/nice-modal-react';
import { post } from '../../axios/axios';
import { useDevice } from '../../hooks/useDevice';
import GroupBuyingOrder from '../../components/GroupBuyingOrder/GroupBuyingOrder';
import {
    clearPackageEntitlement,
    getPackageDocType,
    getSelectedPackageEntitlement,
    isAmountEntitlement,
    selectPackageEntitlement,
} from '../../Service/packageEntitlements';

export const isPptEntitlement = (record) =>
    !!record?.redeemCode &&
    Number(record.codeType) !== 1 &&
    (isAmountEntitlement(record) ||
        (getPackageDocType(record) === 'DEFENSE_PPT' && !record.addServicesModel));

const PPTRedeemModal = NiceModal.create(({ orderNo, initialCode = '' }) => {
    const modal = NiceModal.useModal();
    const { isMobile } = useDevice();
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const busy = useRef(false);
    const isPptOrder = !!orderNo?.startsWith('MLP');

    useEffect(() => {
        if (!modal.visible) return;
        const selected = getSelectedPackageEntitlement();
        const selectedCode = isPptOrder && isPptEntitlement(selected) ? selected.redeemCode : '';
        setCode(initialCode || selectedCode);
        setError('');
        setLoading(false);
        busy.current = false;
    }, [modal.visible, initialCode, isPptOrder, orderNo]);

    const choosePackage = () =>
        NiceModal.show(GroupBuyingOrder, {
            onSelect: (record) => {
                if (!isPptEntitlement(record))
                    throw new Error(
                        '请选择独立的答辩PPT权益或普通金额码；文章套餐附带的PPT请在文章订单中查看',
                    );
                selectPackageEntitlement(record);
                setCode(record.redeemCode);
                setError('');
            },
        }).catch(() => {});

    const handleUseRedeem = async () => {
        const normalized = code.trim();
        if (busy.current) return;
        if (!normalized) {
            setError('请从我的套餐选择权益，或输入已有兑换码');
            return;
        }
        if (!orderNo) {
            setError('当前内容的订单不存在，请返回后重新生成');
            return;
        }
        busy.current = true;
        setLoading(true);
        setError('');
        try {
            const selected = getSelectedPackageEntitlement();
            if (selected?.redeemCode === normalized && Number(selected.codeType) === 1) {
                throw new Error('优惠券用于文章支付抵扣，不能兑换PPT');
            }
            const isAmount = isAmountEntitlement(
                selected?.redeemCode === normalized ? selected : { redeemCode: normalized },
            );
            if (isPptOrder && !isAmount && normalized.startsWith('MLMP')) {
                const detail = await post('/member/redeem/code/detail', { code: normalized });
                if (!detail.success) throw new Error(detail.msg || '查询套餐失败，请重试');
                // Main article packages carry an add-services object. Standalone PPT codes do not.
                if (detail.data && Object.values(detail.data).some((value) => value === true)) {
                    throw new Error(
                        '这是文章套餐，请用于文章生成；套餐附带的PPT可在文章订单中查看',
                    );
                }
            }
            const res = await post('/member/payment/redeemCodeNew', { orderNo, code: normalized });
            if (!res.success || res.data === false)
                throw new Error(res.msg || '使用未完成，请重试');
            if (selected?.redeemCode === normalized) clearPackageEntitlement();
            message.success(isPptOrder ? 'PPT权益已用于当前订单' : '套餐已用于当前内容');
            modal.resolve({ code: normalized, orderNo });
            modal.hide();
        } catch (err) {
            setError(err.response?.data?.msg || err.message || '使用失败，请重试');
        } finally {
            busy.current = false;
            setLoading(false);
        }
    };

    return (
        <Modal
            title="使用已购套餐"
            open={modal.visible}
            width={isMobile ? 'calc(100% - 24px)' : 560}
            footer={null}
            zIndex={1890}
            maskClosable={false}
            closable={!loading}
            onCancel={() => {
                modal.hide();
                modal.reject();
            }}
        >
            <Alert
                type="info"
                showIcon
                message={isPptOrder ? '使用答辩PPT权益或普通金额码' : '将已有兑换码用于当前内容'}
                description={
                    isPptOrder
                        ? '确认后用于当前PPT订单，金额码额度是否足够会在提交时核验。文章套餐附带的PPT请在对应文章订单中查看。'
                        : '请输入适用于当前功能的套餐凭证，确认后开始生成。'
                }
            />
            {isPptOrder && (
                <Button className="mt-4" onClick={choosePackage} disabled={loading}>
                    从我的套餐选择PPT权益或金额码
                </Button>
            )}
            <Space.Compact style={{ width: '100%', margin: '20px 0 12px' }}>
                <Input
                    value={code}
                    disabled={loading}
                    placeholder="输入已有兑换码"
                    onChange={(event) => {
                        setCode(event.target.value);
                        setError('');
                    }}
                    onPressEnter={handleUseRedeem}
                />
            </Space.Compact>
            {error && <Alert type="error" showIcon message={error} className="mb-4" />}
            <Button type="primary" size="large" block loading={loading} onClick={handleUseRedeem}>
                确认使用套餐并生成
            </Button>
        </Modal>
    );
});

export default PPTRedeemModal;
